import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { cardapioService, Cardapio } from '@/services/cardapio'
import { fileService } from '@/services/file'
import { ChefHat, Trash2, Edit2, Plus, FileText, Download, Clock } from 'lucide-react'

export function CardapiosListPage() {
  const [cardapios, setCardapios] = useState<Cardapio[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState<number | null>(null)
  const [downloading, setDownloading] = useState<number | null>(null)

  const loadCardapios = async () => {
    try {
      setLoading(true)
      const response = await cardapioService.getAll()
      
      if (response.success && response.data) {
        setCardapios(response.data)
      } else {
        toast.error(response.error || 'Erro ao carregar cardápios')
      }
    } catch (error: any) {
      console.error('Erro ao carregar cardápios:', error)
      toast.error('Erro ao carregar cardápios')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Tem certeza que deseja excluir este cardápio? Esta ação não pode ser desfeita.')) {
      return
    }

    try {
      setDeleting(id)
      const response = await cardapioService.delete(id)
      
      if (response.success) {
        toast.success('Cardápio excluído com sucesso!')
        await loadCardapios()
      } else {
        toast.error(response.error || 'Erro ao excluir cardápio')
      }
    } catch (error: any) {
      console.error('Erro ao deletar:', error)
      toast.error('Erro ao excluir cardápio')
    } finally {
      setDeleting(null)
    }
  }

  const handleDownload = async (cardapio: Cardapio) => {
    if (!cardapio.id_file) {
      toast.error('Nenhum arquivo disponível para download')
      return
    }

    try {
      setDownloading(cardapio.id_cardapio)
      const fileName = `cardapio_${cardapio.id_cardapio}_${cardapio.nome_cardapio}`
      await fileService.downloadFileById(cardapio.id_file, fileName)
      toast.success('Download concluído com sucesso!')
    } catch (error: any) {
      console.error('Erro ao fazer download:', error)
      toast.error(error.message || 'Erro ao fazer download do arquivo')
    } finally {
      setDownloading(null)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR')
  }

  const formatValidadeDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const isExpired = (validade: string) => {
    const today = new Date()
    const validadeDate = new Date(validade)
    return validadeDate < today
  }

  const getDaysUntilExpiration = (validade: string) => {
    const today = new Date()
    const validadeDate = new Date(validade)
    const timeDiff = validadeDate.getTime() - today.getTime()
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24))
    return daysDiff
  }

  const getValidadeStatus = (validade: string) => {
    const daysUntil = getDaysUntilExpiration(validade)
    
    if (daysUntil < 0) {
      return {
        label: 'Expirado',
        color: 'bg-red-100 text-red-700',
        icon: '❌'
      }
    } else if (daysUntil <= 7) {
      return {
        label: `Expira em ${daysUntil} dias`,
        color: 'bg-yellow-100 text-yellow-700',
        icon: '⚠️'
      }
    } else {
      return {
        label: `Válido por ${daysUntil} dias`,
        color: 'bg-green-100 text-green-700',
        icon: '✅'
      }
    }
  }

  useEffect(() => {
    loadCardapios()
  }, [])

  return (
    <div className="w-full">
      {/* Cabeçalho da página */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <ChefHat className="w-8 h-8 text-orange-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Cardápios</h1>
            <p className="text-gray-600">Gerenciar cardápios e menus</p>
          </div>
        </div>
        
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={() => window.location.href = '/cardapios/novo'}
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Cardápio
          </Button>
        </div>
      </div>

      {/* Conteúdo principal */}
      <Card>
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            <p className="mt-2 text-gray-600">Carregando...</p>
          </div>
        ) : cardapios.length === 0 ? (
          <div className="text-center py-12">
            <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum cardápio encontrado
            </h3>
            <p className="text-gray-600 mb-4">
              Comece adicionando seu primeiro cardápio
            </p>
            <Button
              onClick={() => window.location.href = '/cardapios/novo'}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Cardápio
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {cardapios.map((cardapio) => {
              const validadeStatus = getValidadeStatus(cardapio.validade)
              
              return (
                <div
                  key={cardapio.id_cardapio}
                  className={`border rounded-lg p-6 transition-colors ${
                    isExpired(cardapio.validade) 
                      ? 'border-red-200 bg-red-50' 
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <FileText className="w-6 h-6 text-orange-600" />
                        <h3 className="text-xl font-semibold text-gray-900">
                          {cardapio.nome_cardapio}
                        </h3>
                        <span className={`px-3 py-1 text-sm font-medium rounded-full ${validadeStatus.color}`}>
                          {validadeStatus.icon} {validadeStatus.label}
                        </span>
                      </div>
                      
                      <p className="text-gray-600 mb-4">
                        {cardapio.descricao_cardapio}
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div>
                          <p><strong>Data de Cadastro:</strong></p>
                          <p>{formatDate(cardapio.data_cadastro)}</p>
                        </div>
                        
                        <div>
                          <p><strong>Validade:</strong></p>
                          <p className={`flex items-center space-x-1 ${
                            isExpired(cardapio.validade) ? 'text-red-600 font-medium' : ''
                          }`}>
                            <Clock className="w-4 h-4" />
                            <span>{formatValidadeDate(cardapio.validade)}</span>
                          </p>
                        </div>
                        
                        <div>
                          <p><strong>Última Atualização:</strong></p>
                          <p>{formatDate(cardapio.updatedAt)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(cardapio)}
                        loading={downloading === cardapio.id_cardapio}
                        title="Baixar arquivo"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => window.location.href = `/cardapios/editar/${cardapio.id_cardapio}`}
                        title="Editar cardápio"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(cardapio.id_cardapio)}
                        loading={deleting === cardapio.id_cardapio}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        title="Excluir cardápio"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}
